import { Injectable, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { HttpParams } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { BehaviorSubject, Observable } from 'rxjs';
import { HttpService } from './http.service';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class CommonService {
  public isMobile$ = new BehaviorSubject<boolean>(false);
  public sidebarOpen$ = new BehaviorSubject<boolean>(true);
  public hasNavItems$ = new BehaviorSubject<boolean>(true);
  public viewMode$ = new BehaviorSubject<'table' | 'card'>(this.readInitialViewMode());

  readonly isMobile = toSignal(this.isMobile$, { initialValue: false });
  readonly sidebarOpen = toSignal(this.sidebarOpen$, { initialValue: true });
  readonly viewMode = toSignal(this.viewMode$, { initialValue: 'table' as 'table' | 'card' });
  readonly isCardView = computed(() => this.viewMode() === 'card');

  private readonly authService = inject(AuthService);

  constructor(public http: HttpService) { }

  private readInitialViewMode(): 'table' | 'card' {
    try {
      const raw = localStorage.getItem('appConfig');
      if (raw) {
        const cfg = JSON.parse(raw);
        if (cfg && (cfg.viewMode === 'table' || cfg.viewMode === 'card')) {
          return cfg.viewMode;
        }
      }
    } catch (e) {}
    return 'table';
  }

  setViewMode(mode: 'table' | 'card'): void {
    try {
      const raw = localStorage.getItem('appConfig');
      const cfg = raw ? JSON.parse(raw) : {};
      cfg.viewMode = mode;
      localStorage.setItem('appConfig', JSON.stringify(cfg));
    } catch (e) {}
    this.viewMode$.next(mode);
  }

  apiBaseUrl = environment.apiBaseUrl;

  getDashboardData(): Observable<any> {
    if (this.authService.isRider()) {
      return this.http.get(`${this.apiBaseUrl}/dp/rider/dashboard`);
    }
    if (this.authService.isRepairUser()) {
      return this.http.get(`${this.apiBaseUrl}/contractor/dashboard`);
    }
    return this.http.get(`${this.apiBaseUrl}/dp/dashboard`);
  }

  getMyStoreRatingAndReviews(): Observable<any> {
    if (this.authService.isRider()) {
      return this.http.get(`${this.apiBaseUrl}/dp/rider/reviews`);
    }
    return this.http.get(`${this.apiBaseUrl}/dp/review-list`);
  }

  getReviewDetails(reviewId: string | number): Observable<any> {
    return this.http.get(`${this.apiBaseUrl}/dp/review-details?id=${reviewId}`);
  }
  
  getBanners(): Observable<any> {
    return this.http.get(`${this.apiBaseUrl}/get-contractor-banner`);
  }

  getPolicies(user_type:string, page:string): Observable<any>{
    return this.http.get(`${environment.apiBaseUrl}/get-page?user_type=${user_type}&page=${page}`);
  }

  getFaqs(user_type:string, type?:string): Observable<any>{
    let url = `${environment.apiBaseUrl}/get-faqs?user_type=${user_type}`;
    if (type) url += `&type=${type}`;
    return this.http.get(url);
  }

  getDocuments(employeeId?: number):Observable<any> {
    if(employeeId) {
      let params = new HttpParams().set('id', employeeId);
      return this.http.get(`${environment.apiBaseUrl}/contractor/my-documents-list`, {params: params})
    }else {
      return this.http.get(`${environment.apiBaseUrl}/contractor/my-documents-list`);
    }
  } 

  getCities(): Observable<any> {
    return this.http.get(`${environment.apiBaseUrl}/get-cities`);
  }

  getMyReviews(page:string, employeeId?: any): Observable<any> {
    if(employeeId) {
      return this.http.get(`${environment.apiBaseUrl}/contractor/my-review-list?page=${page}&id=${employeeId}`);
    } else {
      return this.http.get(`${environment.apiBaseUrl}/contractor/my-review-list?page=${page}`);
    }
  }

  getApplicationConfig(): Observable<any> {
    return this.http.get(`${environment.apiBaseUrl}/config-settings`);
  }

  getPolicyDetail(policyUrl: string): Observable<any> {
    return this.http.get(`${environment.apiBaseUrl}/${policyUrl}`);
  }

  getCountries(): Observable<any> {
    return this.http.get(`${environment.apiBaseUrl}/get-countries`);
  }

  getEarningDashboard(type: 'daily' | 'weekly' | 'monthly' | 'yearly' = 'yearly'): Observable<any> {
    if (this.authService.isRider()) {
      const { start_date, end_date } = this.getEarningDateRange(type);
      return this.http.get(
        `${this.apiBaseUrl}/dp/rider/earning-history?type=${type}&start_date=${start_date}&end_date=${end_date}`
      );
    }
    return this.http.get(`${environment.apiBaseUrl}/contractor/earning-dashboard?type=${type}`);
  }

  getEarningStatement(
    start_date: string,
    end_date: string,
    type: 'daily' | 'weekly' | 'monthly' | 'yearly' = 'weekly'
  ): Observable<any> {
    if (this.authService.isRider()) {
      return this.http.get(
        `${this.apiBaseUrl}/dp/rider/earning-history?type=${type}&start_date=${start_date}&end_date=${end_date}`
      );
    }
    return this.http.post(`${environment.apiBaseUrl}/contractor/earning-statement`, {
      start_date,
      end_date,
    });
  }

  private getEarningDateRange(
    type: 'daily' | 'weekly' | 'monthly' | 'yearly'
  ): { start_date: string; end_date: string } {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const end = new Date(today);
    const start = new Date(today);

    if (type === 'daily') {
      // start/end = today
    } else if (type === 'weekly') {
      const day = today.getDay() === 0 ? 7 : today.getDay();
      start.setDate(today.getDate() - (day - 1));
      end.setDate(start.getDate() + 6);
    } else if (type === 'monthly') {
      start.setDate(1);
      end.setMonth(today.getMonth() + 1, 0);
    } else {
      start.setMonth(0, 1);
      end.setMonth(11, 31);
    }

    return {
      start_date: this.formatApiDate(start),
      end_date: this.formatApiDate(end),
    };
  }

  private formatApiDate(d: Date): string {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }
}
